import { iatan2, imax, imin, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { WakeChannel, WakeFork, WakeRules, WakeState } from '../bote-types.ts';
import { ev } from './combat.ts';
import { stepToward } from './enemies.ts';
import { climbAt, crowsAt, gnawHold, gnawTick, hitBoat } from './bote-boat.ts';

/**
 * The wake (docs/sendas/barbaro.md, chapter 3, act 1: «La estela»; the ground in battle/bote-layout.ts). Bera's long boat
 * after the Grey Wolf through drifting floes, six rowers singing. A bar of distance to the Wolf: each second the oars
 * rowed close it, the sea opens it. Every so often the floes part in three channels ahead of the bow, seen a while before
 * the boat is in them: a tap on one and Bera steers for it (none tapped, she takes the widest, as Ivar always does). The
 * Wolf's wake is broad and leaves a grey film of ash; the pack's are thin and cross, and where they swim over his the ash
 * is smeared at a mouth and doesn't go on. The floes drift with the current (the arrows): a wide channel the current is
 * closing is a trap, a narrow one opening a short cut. The right one gains; the pack's wake loses, and they come aboard;
 * a closing one stops the boat, the ice crushes the hull and the wolves climb on. Wolves climb at the oars all along: one
 * at a rowlock bites the oar through and that rower stops till he takes a spare (three of them). Won at the bank of big
 * floes with the Wolf in sight; lost with the bar empty, the boat broken (40 blows) or the hero down.
 *
 * Cost: the forks are the seed's; the biters a short list, looked at once a tick.
 */

const fresh = (r: WakeRules): WakeState => ({
  fork: 0,
  stage: 0,
  at: r.first,
  until: 0,
  pick: -1,
  took: [],
  how: [],
  dist: r.start,
  ivar: r.ivar.start,
  passed: false,
  oars: r.boat.oars.map(() => ({ mode: 0, until: 0 })),
  spares: r.spares,
  bitten: 0,
  biters: [],
  next: r.climb.first,
  crow: r.crows.first,
  gnawCd: r.boat.gnaw,
  done: -1,
  lost: -1,
});

const wr = (s: BattleState): WakeRules | undefined => s.rules?.senda?.bote?.wake;
const ws = (s: BattleState): WakeState | undefined => s.senda?.bote?.wake;

/** The fork in sight (its channels to tap), if the boat isn't in it yet. */
export const wakeFork = (s: BattleState): WakeFork | null => {
  const r = wr(s);
  const w = ws(s);
  if (!r || !w || w.done >= 0 || w.stage !== 0 || w.fork >= r.forks.length || s.waveT < w.at) return null;
  return r.forks[w.fork] ?? null;
};

/** Ticks till the boat is in the fork in sight (−1: none in sight). */
export const wakeLeft = (s: BattleState): number => {
  const r = wr(s);
  const w = ws(s);
  if (!r || !w || !wakeFork(s)) return -1;
  return imax(0, w.at + r.look - s.waveT);
};

/** The oars rowed now. */
export const wakeRowing = (w: WakeState): number => w.oars.filter((o) => o.mode === 0).length;

/** A tap on a channel of the fork in sight: Bera steers for it. */
export const wakeOrder = (s: BattleState, _h: Hero, at: number): void => {
  const w = ws(s);
  const f = wakeFork(s);
  if (!w || !f || at < 0 || at >= f.channels.length) return;
  w.pick = at;
  const c = f.channels[at] as WakeChannel;
  ev(s, { t: 'senda', kind: 'bPick', npc: 'bera', x: c.lane * 2500, y: -9000, n: at });
};

/** How a channel goes: 1 right, 2 the pack's wake, 3 closing on the boat. */
export const channelHow = (c: WakeChannel): number => (!c.ash ? 2 : c.drift < 0 ? 3 : 1);

const take = (s: BattleState, r: WakeRules, w: WakeState, cue: (c: string) => void): void => {
  const f = r.forks[w.fork] as WakeFork;
  const k = w.pick >= 0 ? w.pick : f.wide;
  const c = f.channels[k] as WakeChannel;
  const how = channelHow(c);
  w.took.push(k);
  w.how.push(how);
  w.stage = 1;
  w.until = s.waveT + r.pass;
  ev(s, { t: 'senda', kind: 'bTake', npc: 'bera', x: c.lane * 2500, y: -6000, n: w.fork * 100 + k * 10 + how });
  if (how === 1) {
    w.dist = imin(10000, w.dist + r.gain);
    cue(`right${w.how.filter((x) => x === 1).length}`);
  } else if (how === 2) {
    w.dist = imax(0, w.dist - r.wrong);
    for (let i = 0; i < r.ambush; i++) climbAt(s, r.boat, rngInt(s.rng, r.boat.oars.length), r.wolfHp);
    cue('wrong');
  } else {
    // In a closing channel: the floes shut on the boat, the ice grinds the hull, the boat stopped while they climb on.
    w.until = s.waveT + r.stall;
    hitBoat(s, r.crush, 0, -2500);
    for (let i = 0; i < r.stallWolves; i++) climbAt(s, r.boat, rngInt(s.rng, r.boat.oars.length), r.wolfHp);
    ev(s, { t: 'senda', kind: 'bStall', npc: 'bera', x: 0, y: -3000, n: r.stall });
    cue('stall');
  }
  // Ivar takes the widest, always.
  const iw = f.wide === f.right;
  w.ivar = iw ? imin(10000, w.ivar + r.ivar.gain) : imax(0, w.ivar - r.ivar.wrong);
};

export const wakeTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = wr(s);
  const st = s.senda;
  if (!r || !st) return;
  const bs = (st.bote ??= {});
  const w = (bs.wake ??= fresh(r));
  if (!s.started || w.done >= 0 || w.lost >= 0) return;
  // The oars: a broken one taken over with a spare after a moment, while there are spares.
  w.oars.forEach((o, k) => {
    if (o.mode === 1) {
      w.bitten++;
      cue(`oar${w.bitten}`);
      if (w.spares > 0) {
        w.spares--;
        o.mode = 2;
        o.until = s.tick + r.spare;
      } else o.mode = 3;
    } else if (o.mode === 2 && s.tick >= o.until) {
      o.mode = 0;
      const p = r.boat.oars[k] as { x: number; y: number };
      ev(s, { t: 'senda', kind: 'bSpare', npc: r.boat.rowers[k] ?? '', x: p.x, y: p.y, n: k * 10 + w.spares });
      if (w.spares === 0) cue('lastSpare');
    }
  });
  // The distance, a second at a time: the oars against the sea (none in a closing channel).
  const stalled = w.stage === 1 && w.how[w.how.length - 1] === 3;
  if (s.waveT > 0 && s.waveT % 12 === 0) {
    const rowing = stalled ? 0 : wakeRowing(w);
    w.dist = imax(0, imin(10000, w.dist + rowing * r.row - r.drag));
    w.ivar = imax(0, imin(10000, w.ivar + 6 * r.row - r.drag));
  }
  // The forks.
  if (w.fork < r.forks.length) {
    if (w.stage === 0) {
      if (s.waveT === w.at) {
        ev(s, { t: 'senda', kind: 'bFork', npc: 'bera', x: 0, y: -9000, n: w.fork });
        cue(`fork${w.fork + 1}`);
      }
      if (s.waveT >= w.at + r.look) take(s, r, w, cue);
    } else if (s.waveT >= w.until) {
      w.fork++;
      w.stage = 0;
      w.pick = -1;
      w.at = s.waveT + r.gap;
      if (w.fork >= r.forks.length && w.dist > 0) {
        w.done = s.tick;
        ev(s, { t: 'senda', kind: 'bBank', npc: 'bera', x: 0, y: -9000 });
        cue('bank');
      }
    }
  }
  if (!w.passed && w.dist > w.ivar) {
    w.passed = true;
    ev(s, { t: 'senda', kind: 'bPassIvar', npc: 'ivar', x: 6000, y: -2000 });
    cue('passIvar');
  }
  if (w.dist <= 0) {
    w.lost = s.tick;
    cue('lostWolf');
    return;
  }
  // Wolves climbing at the oars; the ones still going for an oar.
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.climb.every[0], r.climb.every[1]);
    const n = rngRange(s.rng, r.climb.n[0], r.climb.n[1]);
    for (let i = 0; i < n; i++) {
      const open = w.oars.map((o, k) => (o.mode === 0 ? k : -1)).filter((k) => k >= 0);
      const k = open.length ? (open[rngInt(s.rng, open.length)] as number) : rngInt(s.rng, w.oars.length);
      const e = climbAt(s, r.boat, k, r.wolfHp);
      w.biters.push([e.id, k, 0]);
    }
  }
  let j = 0;
  for (const b of w.biters) {
    const e = s.enemies.find((x) => x.id === b[0]);
    const o = w.oars[b[1]];
    if (!e || e.hp <= 0 || !o || o.mode !== 0) continue;
    w.biters[j++] = b;
  }
  w.biters.length = j;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    crowsAt(s, r.crows.n, h.x + 7000, h.y - 3000, r.wolfHp);
  }
  w.gnawCd = gnawTick(s, r.boat, w.gnawCd);
};

/** The wolves going for an oar walk to its rowlock and bite it through; the gnawers at the hull (true: the act moved it). */
export const wakeHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = wr(s);
  const w = ws(s);
  if (!r || !w || e.stun > 0) return false;
  const b = w.biters.find((x) => x[0] === e.id);
  if (b) {
    const o = w.oars[b[1]];
    const p = r.boat.oars[b[1]] as { x: number; y: number };
    if (o && o.mode === 0) {
      if (!withinRange(e.x, e.y, p.x, p.y, r.biteReach)) {
        e.anim = 'walk';
        stepToward(e, p.x, p.y, px(e));
        return true;
      }
      e.facing = iatan2(p.y - e.y, p.x - e.x || 1);
      if (e.anim !== 'attack' || e.animT > 8) {
        e.anim = 'attack';
        e.animT = 0;
      }
      if (++b[2] >= r.bite) {
        o.mode = 1;
        ev(s, { t: 'senda', kind: 'bOar', npc: r.boat.rowers[b[1]] ?? '', x: p.x, y: p.y, n: b[1] });
      }
      return true;
    }
  }
  return gnawHold(s, r.boat, e);
};

/** The forks passed, of all of them. */
export const wakeGoal = (s: BattleState): { done: number; need: number } => {
  const r = wr(s);
  const w = ws(s);
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.done >= 0 ? r.forks.length : w.fork, need: r.forks.length };
};

export const wakeResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = ws(s);
  if (!w) return null;
  if (w.lost >= 0 && s.tick >= w.lost + 24) return 'lost';
  return w.done >= 0 && s.tick >= w.done + 24 ? 'won' : null;
};
